import { InlineComment } from '@amzn/awscat-pattern-library-service-model';
import { useEffect, useMemo, useState } from 'react';

import { useInlineCommentManager } from '../../contexts/InlineCommentManagerContext';
import { stringifyDocId } from '../../model/RichtextDocId';
import { isThreadRoot } from '../../services/InlineCommentManager';
import { InlineTextFilter } from '../../services/InlineTextOperations';
import { isCommentResolved } from '../v2-common/utils/commentStateUtils';

function singleKey(f: InlineTextFilter): string {
  return f.type === 'none' ? 'none' : stringifyDocId(f);
}

function computeKey(filter: InlineTextFilter | readonly InlineTextFilter[]): string {
  if (Array.isArray(filter)) {
    return (filter as readonly InlineTextFilter[]).map(singleKey).join('|');
  }
  return singleKey(filter as InlineTextFilter);
}

interface FilteredInlineCommentsOptions {
  /** When true, only return root comments (thread starters), excluding replies. Defaults to true. */
  readonly rootOnly?: boolean;
  /** When true, exclude comments in terminal states (RESOLVED, IMPLEMENTED, IGNORED, DELETED). Defaults to true. */
  readonly excludeResolved?: boolean;
}

/** Filters inline comments for one or more content regions. Position-only updates from typing do not trigger a re-render. */
export function useFilteredInlineComments(
  filter: InlineTextFilter | readonly InlineTextFilter[],
  options: FilteredInlineCommentsOptions = {},
): readonly InlineComment[] {
  const { rootOnly = true, excludeResolved = true } = options;
  const manager = useInlineCommentManager();
  const key = computeKey(filter);

  // When InlineCommentManager emits 'changed', bump revision to trigger recompute.
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    return manager.on('changed', () => setRevision((r) => r + 1));
  }, [manager]);

  return useMemo(() => {
    const filters = Array.isArray(filter) ? filter : [filter];
    const seen = new Set<string>();
    const results: InlineComment[] = [];

    for (const f of filters) {
      for (const c of manager.filterComments(f)) {
        if (seen.has(c.commentId)) continue;
        if (rootOnly && !isThreadRoot(c)) continue;
        if (excludeResolved && isCommentResolved(c.state)) continue;
        seen.add(c.commentId);
        results.push(c);
      }
    }

    return results;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manager, key, revision, excludeResolved, rootOnly]);
}
